import React, { useState } from 'react';
import { FaCheckCircle, FaSpinner, FaExclamationTriangle, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { buildJobMeta, AuthBadge } from '../../utils/aiMetaUtil';
import { getProviderInfo } from '../../utils/providerMap';
import { useEvidenceName } from '../../../../hooks/useEvidenceName';
import { useJobAccess } from '../../../../hooks/useJobAccess';
import '../../styles/results-ui.css';

const StatusIcon = ({ status }) => {
  if (status === 'processing') return <FaSpinner className="spinning" />;
  if (status === 'completed') return <FaCheckCircle />;
  if (status === 'failed') return <FaExclamationTriangle />;
  return null;
};

/**
 * STANDARDIZED JobCard Component
 * 
 * This is the primary job card component used throughout the application.
 * Any changes here will automatically apply to all locations:
 * - Single Job View
 * - Main Results (OrdersListView)
 * - Jobs History Modal
 * - Similar Analysis Results (via SimilarJobCard wrapper)
 * 
 * Note: SimilarJobCard.js is a legitimate wrapper that adds similarity overlays.
 * DO NOT create other duplicate job card components!
 */
export default function JobCard({ 
  job, 
  resultsMeta = {}, 
  size = 'regular', 
  onOpen, 
  collapsible = false, 
  defaultCollapsed = false,
  isCollapsed: externalIsCollapsed = null, // External collapse state
  onCollapseChange = null, // Optional callback when collapse state changes
  hideDetailsButton = false, // Hide Details button when inline results panel is present
  currentUser = null // Current user for access checking
}) {
  const { provider, model, status, targets } = buildJobMeta(job, resultsMeta);
  const prov = getProviderInfo(job?.provider);
  
  // Use external state if provided, otherwise internal state
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isCollapsed = externalIsCollapsed !== null ? externalIsCollapsed : internalCollapsed;
  
  // Fetch evidence name if we have the required IDs
  const { evidenceName } = useEvidenceName(job?.evidenceId, job?.casefileId);
  
  // Check access type
  const { hasAccess, reason, isOwner } = useJobAccess(currentUser, job);
  
  // Determine access badge
  const getAccessBadge = () => {
    if (!currentUser || !job) return null;
    
    if (isOwner) {
      return { text: 'Owned', color: '#2196f3', icon: '👤' };
    }
    
    if (job.isPublic) {
      return { text: 'Free', color: '#4caf50', icon: '🌐' };
    }
    
    if (reason === 'request_approved') {
      return { text: 'Revocable', color: '#ff9800', icon: '✅' };
    }
    
    if (reason === 'purchase') {
      return { text: 'Purchased', color: '#9c27b0', icon: '💳' };
    }
    
    return null;
  };
  
  const accessBadge = getAccessBadge();
  
  const isCompleted = status === 'completed';
  const isProcessing = status === 'processing';
  const isFailed = status === 'failed';
  
  const statusColor = isCompleted ? '#22c55e' : isProcessing ? '#f59e0b' : isFailed ? '#ef4444' : '#6b7280';
  const cardBg = isCompleted ? 'rgba(34, 197, 94, 0.05)' : isProcessing ? 'rgba(245, 158, 11, 0.05)' : isFailed ? 'rgba(239, 68, 68, 0.05)' : 'rgba(107, 114, 128, 0.05)';

  const handleOpen = () => {
    if (onOpen) onOpen();
  };

  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    
    // Update internal state if not using external state
    if (externalIsCollapsed === null) {
      setInternalCollapsed(newCollapsed);
    }
    
    // Always call the callback if provided
    if (onCollapseChange) {
      onCollapseChange(newCollapsed);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleCardClick = (e) => {
    // Only handle card click if not collapsible, or if collapsible but not clicking on chevron
    if (!collapsible) {
      handleOpen();
    } else {
      // For collapsible cards, clicking the card toggles collapse
      // Full details are only accessible via the results panel button (respects privacy settings)
      handleToggleCollapse();
    }
  };

  const handleChevronClick = (e) => {
    e.stopPropagation();
    handleToggleCollapse();
  };

  const handleDetailsClick = (e) => {
    e.stopPropagation();
    handleOpen();
  };

  return (
    <div 
      className={`job-card-ui ${size} ${collapsible ? 'collapsible' : ''} ${isCollapsed ? 'collapsed' : ''}`} 
      onClick={handleCardClick} 
      style={{ background: cardBg, border: `1px solid ${statusColor}` }}
      data-job-id={job?.id}
    >
      {/* Header Row: Status + Provider + Job ID */}
      <div className="job-header-row">
        <div className="job-header-left">
          {collapsible && (
            <div className="collapse-toggle" style={{ color: statusColor }} onClick={handleChevronClick}>
              {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
            </div>
          )}
          <div className="status-chip" style={{ background: `${statusColor}33` }}>
            <span style={{ color: statusColor }}><StatusIcon status={status} /></span>
            <span>{isCompleted ? 'Completed' : isProcessing ? 'Processing' : isFailed ? 'Failed' : (status || 'Unknown')}</span>
          </div>
          <div className="provider-pill" style={{ color: prov.color }}>
            {prov.icon ? <prov.icon /> : null}
            <span>{provider.name}</span>
          </div>
        </div>
        <div className="job-header-right">
          {accessBadge && (
            <div 
              className="access-badge-compact" 
              style={{ 
                background: `${accessBadge.color}22`,
                border: `1px solid ${accessBadge.color}`,
                color: accessBadge.color
              }}
              title={`Access Type: ${accessBadge.text}`}
            >
              <span className="badge-icon">{accessBadge.icon}</span>
              <span className="badge-text">{accessBadge.text}</span>
            </div>
          )}
          <div className="job-id">Job #{String(job?.id || '').slice(-8)}</div>
          {collapsible && !isCollapsed && !hideDetailsButton && (
            <button 
              className="details-btn" 
              onClick={handleDetailsClick}
              title="View full details"
            >
              Details
            </button>
          )}
        </div>
      </div>

      {/* Collapsible Content */}
      {(!collapsible || !isCollapsed) && (
        <>
          {/* AI Model Row - Prominent display */}
          <div className="ai-model-row">
            <div className="ai-model-info">
              <span className="ai-model-label">AI Model:</span>
              <span className="ai-model-value">{model}</span>
            </div>
          </div>

          {/* Services Row - ALWAYS SHOW FOR REGULAR SIZE */}
          {size === 'regular' && (
            <div className="services-row">
              <span className="services-label">Services:</span>
              <span className="services-value">
                {(() => {
                  // Try multiple sources for services
                  if (Array.isArray(job?.selectedServices) && job.selectedServices.length > 0) {
                    return job.selectedServices
                      .join(', ')
                      .replace(/_/g, ' ')
                      .replace(/-/g, ' ')
                      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize words
                  }
                  if (job?.serviceName && job.serviceName !== 'Standard Analysis') {
                    return job.serviceName.replace(/_/g, ' ').replace(/-/g, ' ');
                  }
                  return 'Standard Analysis';
                })()}
              </span>
            </div>
          )}

          {/* Targets Row */}
          {(() => {
            let targetsToShow = null;
            
            // Try multiple sources for targets in priority order
            if (targets && targets !== '—' && targets.trim() !== '') {
              targetsToShow = targets;
            } else if (resultsMeta?.targets && Array.isArray(resultsMeta.targets) && resultsMeta.targets.length > 0) {
              targetsToShow = resultsMeta.targets.join(', ').replace(/_/g, ' ');
            } else if (job?.analysisRequest?.targets && Array.isArray(job.analysisRequest.targets) && job.analysisRequest.targets.length > 0) {
              targetsToShow = job.analysisRequest.targets.join(', ').replace(/_/g, ' ');
            } else if (job?.selectedTargets && Array.isArray(job.selectedTargets) && job.selectedTargets.length > 0) {
              targetsToShow = job.selectedTargets.join(', ').replace(/_/g, ' ');
            } else if (job?.selectedServices && Array.isArray(job.selectedServices) && job.selectedServices.length > 0) {
              targetsToShow = job.selectedServices.join(', ').replace(/_/g, ' ');
            } else if (job?.serviceName && job.serviceName !== 'Standard Analysis') {
              targetsToShow = job.serviceName.replace(/_/g, ' ');
            }
            
            // Only show targets row if we have targets to display
            return targetsToShow ? (
              <div className="targets-row">
                <span className="targets-label">Targets:</span>
                <span className="targets-value">{targetsToShow}</span>
              </div>
            ) : null;
          })()}

          {/* Footer Row: Evidence ID + Date + Auth */}
          <div className="job-footer-row">
            <div className="footer-left">
              {/* Evidence Name & ID - ALWAYS TRY TO SHOW FOR REGULAR SIZE */}
              {size === 'regular' && job?.evidenceId && (
                <div className="evidence-info">
                  <span className="evidence-label">Evidence:</span>
                  <div className="evidence-details">
                    {evidenceName && (
                      <span className="evidence-name" title={evidenceName}>
                        {evidenceName.length > 25 ? `${evidenceName.substring(0, 25)}...` : evidenceName}
                      </span>
                    )}
                    <span className="evidence-id" title={`Evidence ID: ${job.evidenceId}`}>
                      {job.evidenceId}
                    </span>
                  </div>
                </div>
              )}
              {job?.createdAt && (
                <div className="creation-date">
                  {formatDate(job.createdAt)}
                </div>
              )}
            </div>
            <div className="footer-right">
              <AuthBadge />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
